import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";

type TranslationKeys = keyof typeof translations.en;

interface City {
  _id: string;
  name: string;
}

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes?: string, cityId?: string) => void;
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonColor?: "primary" | "secondary" | "error" | "info" | "success" | "warning";
  loading?: boolean;
  showNotes?: boolean;
  showCitySelection?: boolean;
  cities?: City[];
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  confirmButtonColor = "error",
  loading = false,
  showNotes = false,
  showCitySelection = false,
  cities = [],
}) => {
  const { t } = useTranslation();
  const [notes, setNotes] = React.useState("");
  const [selectedCityId, setSelectedCityId] = React.useState("");

  const handleConfirm = () => {
    onConfirm(showNotes ? notes : undefined, showCitySelection ? selectedCityId : undefined);
    if (showNotes) {
      setNotes(""); // Reset notes after confirmation
    }
    if (showCitySelection) {
      setSelectedCityId(""); // Reset city selection after confirmation
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{message}</Typography>
        {showCitySelection && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("Location" as TranslationKeys)}</InputLabel>
            <Select
              value={selectedCityId}
              label={t("Location" as TranslationKeys)}
              onChange={(e) => setSelectedCityId(e.target.value)}
              required
            >
              {cities.map((city) => (
                <MenuItem key={city._id} value={city._id}>
                  {city.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {showNotes && (
          <TextField
            fullWidth
            label={t("Notes (optional)" as TranslationKeys)}
            multiline
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 160))}
            inputProps={{ maxLength: 160 }}
            helperText={`${notes.length}/160`}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{cancelButtonText || t("Cancel" as TranslationKeys)}</Button>
        <Button
          onClick={handleConfirm}
          color={confirmButtonColor}
          variant="contained"
          disabled={loading || (showCitySelection && !selectedCityId)}
        >
          {confirmButtonText || t("Confirm" as TranslationKeys)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
